import { CheckboxGroup } from '@backrs_ui/common-ui/src/designSystem/checkbox/CheckboxGroup';

import { Box, SxProps, Theme } from '@mui/material';

import { ImageCard } from './ImageCard';

type DescriptionContentProps = Readonly<{
  learnMore?: DataLockedAndLoadedCardProps['learnMore'];
  description: DataLockedAndLoadedCardProps['description'];
}>;

const DescriptionContent = ({ description, learnMore }: DescriptionContentProps) => (
  <>
    {description}
    {learnMore?.text && (
      <Box sx={learnMoreStyles} onClick={learnMore?.onClick}>
        {' '}
        {learnMore.text}
      </Box>
    )}
  </>
);

type CheckboxSectionProps = Readonly<{
  checkboxItems: DataLockedAndLoadedCardProps['checkboxItems'];
}>;

const CheckBoxSection = ({ checkboxItems = [] }: CheckboxSectionProps) => (
  <Box sx={checkboxContainer}>
    <CheckboxGroup
      options={checkboxItems.map((item) => ({ label: item, value: item }))}
      defaultChecked
      disabled
      sxOverrides={cbGroupControlOverrides}
    />
  </Box>
);

export type DataLockedAndLoadedCardProps = Readonly<{
  title: string;
  description: string;
  imageUrl: string;
  checkboxItems?: string[];
  learnMore?: {
    text: string;
    onClick: () => void;
  };
  setMinWidth?: boolean;
}>;

export function DataLockedAndLoadedCard({
  title,
  description,
  checkboxItems = [],
  learnMore,
  imageUrl,
  setMinWidth = true,
}: DataLockedAndLoadedCardProps) {
  return (
    <ImageCard
      imageUrl={imageUrl}
      title={title}
      description={<DescriptionContent description={description} learnMore={learnMore} />}
      sxOverrides={sxOverrides(setMinWidth)}
    >
      {checkboxItems && <CheckBoxSection checkboxItems={checkboxItems} />}
    </ImageCard>
  );
}

const learnMoreStyles: SxProps<Theme> = ({ typography, backrs }) => ({
  display: 'inline',
  color: backrs.colors['Brand/600'],
  cursor: 'pointer',
  ...typography['text-sm-semibold'],
});

const containerSx =
  (setMinWidth: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    ...(setMinWidth && {
      minWidth: {
        xs: '100%',
        md: `calc(50% - ${backrs.spacing['spacing-4xl']})`,
      },
    }),

    ...(!setMinWidth && {
      minWidth: '100%',
    }),

    '&:hover': {
      boxShadow: 'none',
      transform: 'none',
    },
    cursor: 'default',
  });

const descriptionSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const imageSx: SxProps<Theme> = ({ backrs }) => ({
  borderBottom: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  width: 'calc(100% + 2px)',
  marginLeft: '-1px',
  marginRight: '-1px',
});

type sxOverrideType = (setMinWidth: boolean) => Readonly<{
  description?: SxProps<Theme>;
  container?: SxProps<Theme>;
  image?: SxProps<Theme>;
}>;

const sxOverrides: sxOverrideType = (setMinWidth: boolean) => ({
  container: containerSx(setMinWidth),
  description: descriptionSx,
  image: imageSx,
});

const cbGroupControlOverrides: SxProps<Theme> = ({ backrs, typography }) => ({
  outline: 'none',
  padding: backrs.spacing['spacing-none'],
  '&.Mui-disabled .MuiFormControlLabel-label': {
    color: backrs.colors['Colors/Base/Black'],
  },
  '& .MuiFormControlLabel-label': {
    ...typography['text-xs-regular'],
    marginTop: backrs.spacing['spacing-none'],
    paddingTop: backrs.spacing['spacing-none'],
  },
  '& .MuiFormControlLabel-root': {
    display: 'flex',
    marginTop: backrs.spacing['spacing-none'],
    paddingTop: backrs.spacing['spacing-none'],
  },
  '& .MuiCheckbox-root': {
    marginRight: backrs.spacing['spacing-lg'],
    alignSelf: 'flex-start',
  },
  marginLeft: backrs.spacing['spacing-md'],
});

const checkboxContainer: SxProps<Theme> = ({ backrs, typography }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-lg'],
  ...typography['text-xs-regular'],
  paddingTop: backrs.spacing['spacing-2xl'],
});
